/**
 * CollectionMuseum — Phase 2 My Collection redesign.
 *
 * The My Collection page below the header now flows:
 *   1. Collection Overview     (existing summary + overall completion bar)
 *   2. Your Albums             (every owned album, single snapshot list)
 *   3. Hall of Fame            (completed albums only)
 *
 * "Continue Collecting", "Sealed Collections", and "Album Library" are
 * consolidated into "Your Albums". No album appears more than once in
 * that list — completed albums may additionally appear in Hall of Fame.
 *
 * Presentation uses the Phase 1 foundation exclusively:
 *   • buildAlbumPresentation() for titles / cover labels / status.
 *   • <CollectionAlbumSnapshotCard /> for every card.
 * No new progress math is introduced here.
 */
import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  Trophy, Archive, ArchiveRestore, Copy, Edit3, Trash2, MoreHorizontal,
  Album as AlbumIcon, CheckCircle2, Plus, Award,
} from "lucide-react";
import { useDashboardSummary } from "@/components/collector/CollectionLaunchExperience";
import CollectionAlbumSnapshotCard from "@/components/collector/CollectionAlbumSnapshotCard";
import { buildAlbumPresentation } from "@/lib/albumPresentation";

/* ────────────────────────────────────────────────────────────────
 * PUBLIC — CollectionMuseum
 *
 * Props:
 *   albums     : the user's albums array (from MyCollection).
 *   stats      : { albums, in_progress, completed, archived }.
 *   handlers   : { onOpen, onEdit, onDuplicate, onArchive, onRestore, onDelete, onAdd }.
 * ──────────────────────────────────────────────────────────────── */
export default function CollectionMuseum({ albums, stats, handlers }) {
  const { data: summary } = useDashboardSummary();
  const yourAlbums = useMemo(() => sortForDisplay(albums), [albums]);
  const completed = useMemo(
    () => yourAlbums.filter((a) => buildAlbumPresentation(a).status === "completed"),
    [yourAlbums]
  );

  return (
    <div className="space-y-16 md:space-y-20" data-testid="collection-museum">
      <CollectionOverview
        stats={stats}
        certificates={summary?.summary?.certificates ?? 0}
      />
      <YourAlbumsSection albums={yourAlbums} handlers={handlers} />
      <HallOfFameSection albums={completed} handlers={handlers} />
    </div>
  );
}

/**
 * Display sort:
 *   1. In Progress
 *   2. Completed
 *   3. Collection Only
 * Within each group, most recently updated first. Display-only —
 * database order is never mutated.
 */
function sortForDisplay(albums) {
  const rank = { in_progress: 0, completed: 1, collection_only: 2 };
  const active = (albums || []).filter(
    (a) => a.status !== "archived" && a.status !== "sold"
  );
  return active
    .slice()
    .sort((a, b) => {
      const ra = rank[buildAlbumPresentation(a).status] ?? 99;
      const rb = rank[buildAlbumPresentation(b).status] ?? 99;
      if (ra !== rb) return ra - rb;
      const ta = Date.parse(a.updated_at || a.created_at || 0) || 0;
      const tb = Date.parse(b.updated_at || b.created_at || 0) || 0;
      return tb - ta;
    });
}

/* ══════════════════════════════════════════════════════════════════
 * SECTION 1 · Collection Overview
 *
 * Phase C2a — the customer-facing "Overall Completion" progress bar
 * was removed here per §1. The four canonical snapshot metrics
 * (Albums Owned · Completed · In Progress · Certificates) remain.
 * Backend `overall_completion` is still returned for external
 * consumers but no longer surfaced in the UI.
 * ══════════════════════════════════════════════════════════════════ */
function CollectionOverview({ stats, certificates }) {
  const cells = [
    { label: "Albums Owned", value: stats?.albums ?? 0, tone: "gold" },
    { label: "Completed",   value: stats?.completed ?? 0, tone: "emerald" },
    { label: "In Progress", value: stats?.in_progress ?? 0, tone: "cream" },
    { label: "Certificates", value: certificates,        tone: "gold" },
  ];
  return (
    <section data-testid="myc-overview" className="relative">
      <SectionHeading
        eyebrow="Your Museum"
        title="Collection Overview"
        subtitle="A snapshot of every album you own."
      />
      <div className="mt-6 rounded-3xl border-2 border-[var(--ink)] bg-white shadow-[8px_8px_0_var(--ink)] p-6 md:p-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {cells.map((c) => (
            <GlanceCell key={c.label} {...c} testid={`myc-overview-${slug(c.label)}`} />
          ))}
        </div>
      </div>
    </section>
  );
}

function GlanceCell({ label, value, tone, testid }) {
  const palette = {
    gold:    { border: "border-[#c8a24d]",   bg: "bg-white",                        num: "text-[#8a6a1f]",     chip: "bg-[#fff3d6]" },
    emerald: { border: "border-emerald-900", bg: "bg-white",                        num: "text-emerald-900",   chip: "bg-emerald-50" },
    cream:   { border: "border-[var(--ink)]", bg: "bg-[var(--panini-yellow-soft)]", num: "text-[var(--ink)]",  chip: "bg-white" },
  }[tone];
  return (
    <div className={`rounded-2xl border-2 ${palette.border} ${palette.bg} p-5`} data-testid={testid}>
      <div className={`inline-block px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.24em] font-bold ${palette.chip} text-[var(--ink)]`}>
        {label}
      </div>
      <div className={`mt-3 font-display font-bold text-4xl md:text-5xl ${palette.num}`}>{value}</div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * SECTION 2 · Your Albums
 *
 * Every owned album appears here once. Uses the shared snapshot card
 * so tracked vs collection-only rendering is guaranteed consistent
 * with My Profile, Album Detail, and Hall of Fame.
 * ══════════════════════════════════════════════════════════════════ */
function YourAlbumsSection({ albums, handlers }) {
  return (
    <section data-testid="myc-your-albums">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <SectionHeading
          icon={AlbumIcon}
          eyebrow="Your Library"
          title="Your Albums"
          subtitle="Every album you own — in progress, completed, and collection-only."
        />
        {handlers?.onAdd && (
          <button
            type="button"
            onClick={handlers.onAdd}
            data-testid="myc-your-albums-add"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-[var(--panini-red)] text-white px-5 py-2.5 text-xs font-black uppercase tracking-widest shadow-[4px_4px_0_var(--ink)] hover:brightness-110 transition"
          >
            <Plus size={14} /> Add Album
          </button>
        )}
      </div>

      {albums.length === 0 ? (
        <div
          className="mt-8 rounded-2xl border-2 border-dashed border-[var(--ink)] bg-white p-10 text-center"
          data-testid="myc-your-albums-empty"
        >
          <p className="text-sm text-[var(--smoke)]">
            No albums yet — add your first album to get started.
          </p>
        </div>
      ) : (
        <div
          className="mt-8 grid gap-5 md:grid-cols-2"
          data-testid="myc-your-albums-list"
        >
          {albums.map((a) => (
            <div key={a.id} data-testid={`myc-album-card-${a.id}`}>
              <CollectionAlbumSnapshotCard
                album={a}
                layout="horizontal"
                onOpen={() => handlers?.onOpen?.(a)}
                manageSlot={<ManageMenu album={a} handlers={handlers} tone="light" />}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * SECTION 3 · Hall of Fame — completed albums only.
 * ══════════════════════════════════════════════════════════════════ */
function HallOfFameSection({ albums, handlers }) {
  return (
    <section data-testid="myc-hall-of-fame" className="relative">
      <div
        aria-hidden
        className="absolute inset-x-0 -top-6 h-40 opacity-40 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(200,162,77,0.25), transparent 60%)",
        }}
      />
      <SectionHeading
        icon={Trophy}
        eyebrow="Achievement"
        title="Hall of Fame"
        subtitle="Verified completions permanently displayed in your museum."
        accent="gold"
      />
      {albums.length === 0 ? (
        <div
          className="mt-8 rounded-3xl border-2 border-dashed border-[#c8a24d]/60 bg-white p-10 text-center"
          data-testid="myc-hall-of-fame-empty"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-[#fff3d6] grid place-items-center">
            <Trophy size={28} className="text-[#8a6a1f]" />
          </div>
          <h3 className="mt-4 font-display text-2xl md:text-3xl">
            Your first trophy is waiting.
          </h3>
          <p className="mt-2 text-sm text-[var(--smoke)] max-w-md mx-auto">
            Complete an album to earn a gold trophy card in your permanent Hall of Fame.
          </p>
        </div>
      ) : (
        <div
          className="mt-8 grid gap-5 md:grid-cols-2"
          data-testid="myc-hall-of-fame-list"
        >
          {albums.map((a) => (
            <div key={a.id} data-testid={`myc-hof-card-${a.id}`}>
              <CollectionAlbumSnapshotCard
                album={a}
                variant="trophy"
                layout="horizontal"
                onOpen={() => handlers?.onOpen?.(a)}
                footer={
                  a.certificate_number ? (
                    <a
                      href={`/certificates/${a.certificate_number}`}
                      className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-widest text-emerald-900 hover:underline"
                      data-testid={`myc-hof-certificate-${a.id}`}
                    >
                      <Award size={12} /> View Certificate
                    </a>
                  ) : null
                }
                manageSlot={<ManageMenu album={a} handlers={handlers} tone="gold" />}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════
 * Shared bits
 * ══════════════════════════════════════════════════════════════════ */
function SectionHeading({ icon: Icon, eyebrow, title, subtitle, accent }) {
  const isGold = accent === "gold";
  return (
    <header className="max-w-2xl">
      <div
        className={
          "inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] font-bold " +
          (isGold ? "text-[#8a6a1f]" : "text-[var(--panini-red)]")
        }
      >
        {Icon && <Icon size={13} />} {eyebrow}
      </div>
      <h2 className="mt-3 font-display font-bold leading-[1.05] text-3xl md:text-4xl text-[var(--ink)]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-sm md:text-base text-[var(--smoke)]">{subtitle}</p>
      )}
    </header>
  );
}

function ManageMenu({ album, handlers, tone = "light" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const isArchived = album.status === "archived";
  const btn =
    tone === "gold"
      ? "text-[#8a6a1f] hover:text-[#0a0f1c] bg-white border-2 border-[#c8a24d]"
      : "text-[var(--ink)] hover:bg-neutral-50 bg-white border-2 border-[var(--ink)]";

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Manage album"
        data-testid={`myc-manage-${album.id}`}
        className={`inline-flex items-center gap-1.5 rounded-full ${btn} px-3 py-2 text-[10px] uppercase tracking-widest font-bold`}
      >
        <MoreHorizontal size={13} /> Manage
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 bottom-full mb-2 z-30 min-w-[190px] rounded-2xl border-2 border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--ink)] overflow-hidden"
        >
          <MenuBtn
            onClick={() => { setOpen(false); handlers?.onEdit?.(album); }}
            icon={Edit3}
            label="Edit details"
            testid={`myc-manage-${album.id}-edit`}
          />
          <MenuBtn
            onClick={() => { setOpen(false); handlers?.onDuplicate?.(album); }}
            icon={Copy}
            label="Duplicate"
            testid={`myc-manage-${album.id}-duplicate`}
          />
          {isArchived ? (
            <MenuBtn
              onClick={() => { setOpen(false); handlers?.onRestore?.(album); }}
              icon={ArchiveRestore}
              label="Restore"
              testid={`myc-manage-${album.id}-restore`}
            />
          ) : (
            <MenuBtn
              onClick={() => { setOpen(false); handlers?.onArchive?.(album); }}
              icon={Archive}
              label="Archive"
              testid={`myc-manage-${album.id}-archive`}
            />
          )}
          <MenuBtn
            onClick={() => { setOpen(false); handlers?.onDelete?.(album); }}
            icon={Trash2}
            label="Delete"
            tone="danger"
            testid={`myc-manage-${album.id}-delete`}
          />
        </div>
      )}
    </div>
  );
}

function MenuBtn({ onClick, icon: Icon, label, tone, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      role="menuitem"
      className={
        "w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left border-b border-[var(--stone)] last:border-b-0 " +
        (tone === "danger"
          ? "text-red-800 hover:bg-red-50"
          : "text-[var(--ink)] hover:bg-neutral-50")
      }
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
